import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  UserGroupIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const StaffDirectoryPage = () => {
  const leadership = [
    { name: 'Dr. Tracy Smith', role: 'Principal', email: 'tsmith148@schools.nyc.gov' },
    { name: 'Mrs. Lizzy Rodriguez', role: 'Assistant Principal', email: 'LRivera12@schools.nyc.gov' },
    { name: 'Mrs. Eva Lugo', role: 'Assistant Principal', email: 'ELugo7@schools.nyc.gov' },
    { name: 'Ms. Brianna Hawkins', role: 'Dean', email: 'bhawkins4@schools.nyc.gov' },
    { name: 'Ms. Talyiah Nickens', role: 'Parent Coordinator', email: 'tnickens2@schools.nyc.gov' },
    { name: 'Ms. Paola Jose', role: "Principal's Secretary", email: 'DJosedeabreu@schools.nyc.gov' },
    { name: 'Mrs. Luz Vasquez', role: 'Elementary School Guidance Counselor', email: 'LVasquez2@schools.nyc.gov' },
    { name: 'Ms. Octavia Jackson', role: 'Middle School Guidance Counselor', email: 'OJackson9@schools.nyc.gov' },
    { name: 'Ms. Victoria Orlik', role: 'IEP Coordinator', email: 'VOrlik@schools.nyc.gov' }
  ];

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* ============================================================================
            HERO SECTION
        ============================================================================ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
             <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
             <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
             {/* Decorative elements */}
             <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
             <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
               <UserGroupIcon className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              School Leadership <span className="text-secondary">Directory</span>
            </h1>
            <p className="text-xl text-white/80 font-light">
              Reach our leadership team directly
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* ============================================================================
            LEADERSHIP DIRECTORY
        ============================================================================ */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Section Header */}
              <div className="bg-blue-50 text-blue-700 border-blue-200 border-b-2 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <UserGroupIcon className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <h2 className="text-2xl font-bold">School Leadership Team</h2>
                </div>
              </div>

              {/* Leadership List */}
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {leadership.map((person) => (
                    <div
                      key={person.email}
                      className="group bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-primary/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                          <UserGroupIcon className="w-6 h-6 text-primary" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-lg group-hover:text-primary transition-colors">
                            {person.name}
                          </h3>
                          <p className="text-sm text-gray-600 font-medium mb-3">
                            {person.role}
                          </p>
                          <a
                            href={`mailto:${person.email}`}
                            className="inline-flex items-center gap-2 text-orange-700 hover:text-orange-800 font-medium text-sm transition-colors break-all"
                          >
                            <EnvelopeIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate">{person.email}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* General inquiries */}
            <div className="mt-8 bg-primary text-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
              <div className="relative z-10">
                <h2 className="text-xl font-bold mb-2">Looking for a teacher or another staff member?</h2>
                <p className="text-white/80 mb-6 max-w-2xl">
                  Our main office can connect you with any member of the school community.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:212-860-5831"
                    className="bg-white text-primary px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
                  >
                    Call 212-860-5831
                  </a>
                  <a
                    href="/contact"
                    className="bg-white/10 hover:bg-white/20 border border-white/30 px-6 py-3 rounded-full font-bold transition-colors"
                  >
                    Contact the School
                  </a>
                </div>
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StaffDirectoryPage;
